import { createContext, useContext, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode, type Ref } from "react";
import type { SettingsResponse } from "@apphq/shared/types";
import { AppHQLogo } from "../Logo";
import { getApps, getInbox, getSettings, type AdminAppRow } from "../lib/api-admin";
import { relativeNl } from "../lib/format";
import { isTabActive, nextSearchOpen } from "../lib/nav-logic";
import { Link, navigate, useLocation } from "../lib/router";
import { usePrefs } from "../lib/prefs";
import { useAsync } from "../lib/useAsync";
import { AppIcon, Dot } from "./ui";

interface ShellData {
  /** Visible, non-archived apps (sidebar, lists). */
  apps: AdminAppRow[];
  /** Same plus hidden apps, for Settings only. */
  settingsApps: AdminAppRow[];
  inboxCount: number;
  settings: SettingsResponse | null;
  /** "error" = settings could not be loaded (unknown), distinct from auto-link being off. */
  settingsState: "loading" | "error" | "ready";
  refresh: () => void;
}

const ShellCtx = createContext<ShellData>({ apps: [], settingsApps: [], inboxCount: 0, settings: null, settingsState: "loading", refresh: () => {} });
export const useShell = (): ShellData => useContext(ShellCtx);

/** Single source for navigation. Desktop: "main" in the sidebar, "foot" at its bottom. Mobile: `tab` items form the bottom tab bar; the rest live in the header. */
const NAV: Array<{ to: string; label: string; icon: string; area: "main" | "foot"; tab: boolean }> = [
  { to: "/", label: "Overzicht", icon: "▦", area: "main", tab: true },
  { to: "/gebruik", label: "Gebruik", icon: "◔", area: "main", tab: true },
  { to: "/klanten", label: "Klanten", icon: "◉", area: "main", tab: true },
  { to: "/inbox", label: "Inbox", icon: "✉", area: "main", tab: false },
  { to: "/instellingen", label: "Instellingen", icon: "⚙", area: "foot", tab: true },
];
const MAIN_NAV = NAV.filter((n) => n.area === "main");
const FOOT_NAV = NAV.filter((n) => n.area === "foot");
const TAB_NAV = NAV.filter((n) => n.tab);
const INBOX = NAV.find((n) => n.to === "/inbox")!;

export function AppShell({ children }: { children: ReactNode }) {
  const apps = useAsync(() => getApps(true), []);
  const inbox = useAsync(() => getInbox(), []);
  const settings = useAsync(() => getSettings(), []);

  const value = useMemo<ShellData>(
    () => ({
      apps: apps.data?.apps.filter((a) => a.status !== "archived" && !a.hidden) ?? [],
      settingsApps: apps.data?.apps.filter((a) => a.status !== "archived") ?? [],
      inboxCount: inbox.data?.items.length ?? 0,
      settings: settings.data ?? null,
      settingsState: settings.status === "error" && !settings.data ? "error" : settings.data ? "ready" : "loading",
      refresh: () => {
        apps.reload();
        inbox.reload();
        settings.reload();
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apps.data, inbox.data, settings.data, settings.status],
  );

  return (
    <ShellCtx.Provider value={value}>
      <div className="shell">
        <MobileHeader />
        <Sidebar />
        <main className="main">{children}</main>
        <TabBar />
      </div>
    </ShellCtx.Provider>
  );
}

function useThemeCycle() {
  const { theme, set } = usePrefs();
  const next = theme === "auto" ? "dark" : theme === "dark" ? "light" : "auto";
  const label = theme === "auto" ? "Automatisch" : theme === "dark" ? "Donker" : "Licht";
  return { label, cycle: () => set({ theme: next }) };
}

function SearchForm({ inputRef, onDone }: { inputRef?: Ref<HTMLInputElement>; onDone?: () => void }) {
  const [q, setQ] = useState("");
  function onSearch(e: FormEvent) {
    e.preventDefault();
    onDone?.();
    const s = q.trim();
    navigate(s ? `/klanten?q=${encodeURIComponent(s)}` : "/klanten");
  }
  return (
    <form onSubmit={onSearch} role="search">
      <input ref={inputRef} className="search" type="search" placeholder="Zoek klant- of transactie-ID" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Zoek klant of transactie" />
    </form>
  );
}

/** Desktop sidebar. Hidden on mobile (CSS), where MobileHeader + TabBar take over. */
function Sidebar() {
  const { path } = useLocation();
  const { apps, inboxCount, settings, settingsState } = useShell();
  const { label: themeLabel, cycle } = useThemeCycle();

  return (
    <aside className="sidebar glass" aria-label="Navigatie">
      <Link to="/" className="brand">
        <AppHQLogo size={30} />
        <span>AppHQ</span>
      </Link>
      <SearchForm />

      <nav>
        <div className="nav-title">Algemeen</div>
        {MAIN_NAV.map((n) => (
          <Link key={n.to} to={n.to} className={isTabActive(n.to, path) ? "nav-item active" : "nav-item"} aria-current={isTabActive(n.to, path) ? "page" : undefined}>
            <span className="nav-icon" aria-hidden>
              {n.icon}
            </span>
            <span>{n.label}</span>
            {n.to === INBOX.to && inboxCount > 0 && <span className="badge-count">{inboxCount}</span>}
          </Link>
        ))}

        <div className="nav-title">Apps</div>
        {apps.length === 0 && <div className="nav-empty">Nog geen apps. Nieuwe apps verschijnen hier automatisch.</div>}
        {apps.map((a) => {
          const to = `/apps/${encodeURIComponent(a.id)}`;
          const active = path === to;
          return (
            <Link key={a.id} to={to} className={active ? "nav-item active" : "nav-item"} aria-current={active ? "page" : undefined}>
              <AppIcon name={a.display_name ?? a.name} url={a.icon_url} size={22} seed={a.id} />
              <span className="nav-app-name">{a.display_name ?? a.name}</span>
            </Link>
          );
        })}
      </nav>

      <div className="sidebar-foot">
        <div className="sync-card">
          <Dot tone={settings ? (settings.asc_keys ? "good" : "warn") : "neutral"} />
          <div>
            <strong>{settings ? (settings.asc_keys ? "Auto-koppeling actief" : "Auto-koppeling uit") : settingsState === "error" ? "Status onbekend" : "Auto-koppeling controleren…"}</strong>
            <div className="muted">
              {settings ? (settings.asc_keys ? `Laatste sync: ${relativeNl(settings.last_asc_sync)}` : "App Store Connect-sleutel ontbreekt") : settingsState === "error" ? "Instellingen konden niet worden geladen" : " "}
            </div>
          </div>
        </div>
        {FOOT_NAV.map((n) => (
          <Link key={n.to} to={n.to} className={isTabActive(n.to, path) ? "nav-item active" : "nav-item"}>
            <span className="nav-icon" aria-hidden>
              {n.icon}
            </span>
            <span>{n.label}</span>
          </Link>
        ))}
        <button type="button" className="nav-item nav-button" onClick={cycle} title="Wissel tussen automatisch, donker en licht">
          <span className="nav-icon" aria-hidden>
            ◐
          </span>
          <span>Weergave: {themeLabel}</span>
        </button>
      </div>
    </aside>
  );
}

/** Mobile top bar: brand, search, appearance and inbox (with badge). Hidden on desktop (CSS). */
function MobileHeader() {
  const { path } = useLocation();
  const { inboxCount } = useShell();
  const { label: themeLabel, cycle } = useThemeCycle();
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setSearchOpen((o) => nextSearchOpen(o, "navigate"));
  }, [path]);

  useEffect(() => {
    if (!searchOpen) return;
    searchRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setSearchOpen((o) => nextSearchOpen(o, "escape"));
        toggleRef.current?.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [searchOpen]);

  const inboxActive = isTabActive(INBOX.to, path);

  return (
    <header className="m-header glass">
      <div className="m-header-row">
        <Link to="/" className="brand">
          <AppHQLogo size={28} />
          <span>AppHQ</span>
        </Link>
        <div className="m-actions">
          <button ref={toggleRef} type="button" className="icon-btn" aria-label="Zoeken" aria-expanded={searchOpen} aria-controls="m-search" onClick={() => setSearchOpen((o) => nextSearchOpen(o, "toggle"))}>
            <span aria-hidden>⌕</span>
          </button>
          <button type="button" className="icon-btn" onClick={cycle} aria-label={`Weergave: ${themeLabel}. Wisselen`}>
            <span aria-hidden>◐</span>
          </button>
          <Link to={INBOX.to} className={inboxActive ? "icon-btn active" : "icon-btn"} aria-label={inboxCount > 0 ? `Inbox, ${inboxCount} items` : "Inbox"} aria-current={inboxActive ? "page" : undefined}>
            <span aria-hidden>{INBOX.icon}</span>
            {inboxCount > 0 && (
              <span className="icon-badge" aria-hidden>
                {inboxCount > 99 ? "99+" : inboxCount}
              </span>
            )}
          </Link>
        </div>
      </div>
      {searchOpen && (
        <div id="m-search" className="m-search">
          <SearchForm inputRef={searchRef} onDone={() => setSearchOpen((o) => nextSearchOpen(o, "submit"))} />
        </div>
      )}
    </header>
  );
}

/** Mobile bottom tab bar. Hidden on desktop (CSS). */
function TabBar() {
  const { path } = useLocation();
  return (
    <nav className="tabbar" aria-label="Hoofdnavigatie">
      {TAB_NAV.map((n) => {
        const active = isTabActive(n.to, path);
        return (
          <Link key={n.to} to={n.to} className={active ? "tab active" : "tab"} aria-current={active ? "page" : undefined}>
            <span className="tab-icon" aria-hidden>
              {n.icon}
            </span>
            <span className="tab-label">{n.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

/** Glass page header: title + subtitle on the left, the screen's controls on the right. */
export function PageHeader({ title, subtitle, back, children }: { title: ReactNode; subtitle?: ReactNode; back?: { to: string; label: string }; children?: ReactNode }) {
  return (
    <header className="page-header glass">
      <div className="page-title">
        {back && (
          <Link to={back.to} className="back" aria-label={back.label}>
            ‹ {back.label}
          </Link>
        )}
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {children && <div className="page-controls">{children}</div>}
    </header>
  );
}
